import {
  NOW,
  CURRENT_DAY,
  CURRENT_WEEKDAY,
  CURRENT_MONTH,
  CURRENT_YEAR,
  MONTHS
} from './constants';
import { formatDate } from './format';

/**
 * @typedef {import('./format.js').Format} formatType
 */

/**
 * Возвращает дату первого дня текущего месяца в нужном формате
 * @param {formatType} [format="number"] - формат; default: 'number'
 * @returns {string}
 */
export function getFirstDayDate(format = 'number') {
  let firstDayDate = new Date(CURRENT_YEAR, CURRENT_MONTH, 1);
  return formatDate(firstDayDate, format);
}

/**
 * Возвращает дату последнего дня текущего месяца в нужном формате
 * @param {formatType} [format="number"] - формат; default: 'number'
 * @returns {string}
 */
export function getLastDayDate(format = 'number') {
  let lastDayDate = new Date(CURRENT_YEAR, CURRENT_MONTH + 1, 0);
  return formatDate(lastDayDate, format);
}

/**
 * Возвращает дату, соответствующую ближайшему дню, в нужном формате
 * @param {string[]} days - дни для выборки ближайшего
 * @param {boolean} includeLastDay - включать ли последний день текущего месяца
 * @param {Format} [format="number"] - формат; default: 'number'
 * @returns {string}
 */
export function getClosestDate(
  days,
  includeLastDay = false,
  format = 'number'
) {
  let targetDays = includeLastDay
    ? [...days, Number(getLastDayDate().split('.')[0])]
    : days;

  if (targetDays.length === 0) {
    throw new Error('Необходимо добавить хотя бы один день для выборки');
  }

  let futureDates = targetDays.map((day) => {
    return day > CURRENT_DAY
      ? new Date(CURRENT_YEAR, CURRENT_MONTH, day)
      : new Date(CURRENT_YEAR, CURRENT_MONTH + 1, day);
  });

  let sortedDates = futureDates.sort((a, b) => a - b);

  return formatDate(sortedDates[0], format);
}

/**
 * Возвращает текущий месяц с нужным окончанием
 * @param {string} ending - окончание, можно передавать как одно окончание, так и комбинацию
 * @returns {string}
 * @example
 * getMonthByEnding() // 'январь'
 * getMonthByEnding('а-я') // 'января'
 * getMonthByEnding('а-я') // 'марта'
 * getMonthByEnding('а') // 'марта'
 * getMonthByEnding('у-ю') // 'январю'
 * getMonthByEnding('у') // 'марту'
 * getMonthByEnding('ю') // 'январю'
 * getMonthByEnding('ю') // 'марту'
 * getMonthByEnding('е') // 'январе'
 */
export function getMonthByEnding(ending = 'default') {
  const mode = Object.keys(MONTHS).find(
    (key) => key === ending || key.includes(ending)
  );

  if (mode) {
    return MONTHS[mode][CURRENT_MONTH];
  }
  console.error('Нет такого окончания в @/utils/date/constants.js');
  return MONTHS.default[CURRENT_MONTH];
}

/**
 * Возвращает дату в зависимости от дня недели в нужном формате
 * @param {string} weekday - индекс дня недели (пн - 0, вс - 6)
 * @param {Format} [format="number"] - формат; default: 'number'
 * @returns {string}
 */
export function getDateByWeekday(weekday, format = 'number') {
  if (weekday < 0 || weekday > 6) {
    throw new Error(
      `День недели - ${weekday} не существует. Передайте индекс дня недели (0-6)`
    );
  }

  let difference = weekday - CURRENT_WEEKDAY;

  if (difference <= 0) {
    difference += 7;
  }

  const today = NOW;
  today.setDate(CURRENT_DAY + difference);

  return formatDate(today, format);
}
